import React, { useState } from "react"
import { FormGroup, Label, FormFeedback, InputGroup, Input, Button } from "reactstrap"

const PasswordInput = ({
    label,
    name,
    placeholder,
    validation,
    helperText,
    readOnly = false,
    required = true,
    maxLength,
    className = "",
    inputClassName = "",
    handleChange,
    emptyText,
    showToggle = true, // Option to show/hide the eye button
    onToggleShow, // Callback for when show/hide is clicked in readonly mode
    ...rest
}) => {
    const [showPassword, setShowPassword] = useState(false)
    
    const showHelperText = !readOnly && (helperText || maxLength)
    const hasError = validation.touched[name] && validation.errors[name]
    const currentLength = validation.values[name]?.length || 0
    const remainingChars = maxLength ? maxLength - currentLength : null
    const hasValue =
        validation.values[name] && validation.values[name].toString().trim() !== ""

    // Check if the field is optional by looking at the validation schema
    const isRequired = required && !readOnly

    // Determine helper text to show
    const getHelperText = () => {
        if (maxLength) {
            const charText = `${remainingChars} ${remainingChars === 1 ? "character" : "characters"
                } remaining`
            return helperText ? `${helperText} (${charText})` : charText
        }
        return helperText
    }

    // Get display value for readonly mode
    const getDisplayValue = () => {
        if (readOnly && !hasValue) {
            const fieldName = label ? label.replace("*", "").trim() : name
            return emptyText || `${fieldName} not found`
        }
        return validation.values[name] || ""
    }

    return (
        <FormGroup
            className={`mb-3 ${showHelperText ? "info-text-input-container" : ""
                } ${className}`}
        >
            {label && (
                <Label htmlFor={name}>
                    {label}
                    {isRequired ? "*" : ""}
                </Label>
            )}

            {readOnly ? (
                showToggle ? (
                    <InputGroup>
                        <Input
                            id={name}
                            name={name}
                            type={showPassword ? "text" : "password"}
                            value={getDisplayValue()}
                            readOnly={readOnly}
                            className={`input-as-text ${inputClassName} ${readOnly && !hasValue ? "text-danger" : ""}`}
                        />
                        <Button
                            color="info"
                            size="sm"
                            onClick={() => {
                                const newShowState = !showPassword
                                if (onToggleShow) {
                                    onToggleShow(newShowState)
                                }
                                setShowPassword(newShowState)
                            }}
                            type="button"
                            title={showPassword ? "Hide" : "Show"}
                        >
                            <i className={showPassword ? "mdi mdi-eye-off" : "mdi mdi-eye"}></i>
                        </Button>
                    </InputGroup>
                ) : (
                    <Input
                        id={name}
                        name={name}
                        type={showPassword ? "text" : "password"}
                        value={getDisplayValue()}
                        readOnly={readOnly}
                        className={`form-control input-as-text ${inputClassName} ${readOnly && !hasValue ? "text-danger" : ""}`}
                    />
                )
            ) : (
                <InputGroup>
                    <Input
                        id={name}
                        name={name}
                        type={showPassword ? "text" : "password"}
                        placeholder={placeholder}
                        value={validation.values[name] || ""}
                        onChange={handleChange || validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={hasError ? true : false}
                        maxLength={maxLength}
                        className={inputClassName}
                        {...rest}
                    />
                    {showToggle && (
                        <Button
                            color="info"
                            size="sm"
                            onClick={() => {
                                const newShowState = !showPassword
                                if (onToggleShow) {
                                    onToggleShow(newShowState)
                                }
                                setShowPassword(newShowState)
                            }}
                            type="button"
                            title={showPassword ? "Hide" : "Show"}
                        >
                            <i className={showPassword ? "mdi mdi-eye-off" : "mdi mdi-eye"}></i>
                        </Button>
                    )}
                </InputGroup>
            )}

            {hasError ? (
                <FormFeedback type="invalid" className="d-block">{validation.errors[name]}</FormFeedback>
            ) : null}

            {showHelperText && (
                <span
                    className={`text-muted text-end ${readOnly ? "d-none" : "d-inline-block"
                        } ${hasError ? "w-50" : "w-100"}`}
                >
                    {getHelperText()}
                </span>
            )}
        </FormGroup>
    )
}

export default PasswordInput